import React, { useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import LockIcon from '@mui/icons-material/Lock'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import usePermissions from '@/utils/hooks/users'
import AccessibleTooltip from './AccessibleTooltip'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { getEffectiveOrgUuid } from '@/utils/utils'
import { useRequestUpgrade } from '@/utils/hooks/subscriptions'
import { requestUpgradeErrorMessage } from '@/api/subscriptionsAPI'
import { UUID } from 'crypto'

// White tooltip styling props for AccessibleTooltip
const whiteTooltipProps = {
  arrow: true,
  slotProps: {
    tooltip: {
      sx: {
        backgroundColor: '#ffffff',
        color: '#333333',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        fontSize: '0.875rem',
        border: '1px solid #e0e0e0',
      },
    },
    arrow: {
      sx: {
        color: '#ffffff',
        '&::before': {
          border: '1px solid #e0e0e0',
        },
      },
    },
  },
}

interface UpgradeCTAProps {
  variant?: 'inline' | 'banner' | 'dialog'
  message?: string
  isOpen?: boolean
  onClose?: () => void
  ctaText?: string
  restrictedFeature?: string
}

// Constants for role-based messaging
const ADMIN_ONLY_TOOLTIP = 'Only organisation admins can upgrade'
const CONTACT_ADMIN_MESSAGE =
  'Please contact your organisation admin to upgrade your account.'
const NOT_ADMIN_DIALOG_MESSAGE =
  "You're not an admin for this organisation, so only an admin can upgrade the plan. Send them a request below."
const REQUEST_UPGRADE_SUCCESS_MESSAGE =
  "Your upgrade request has been sent to your organisation's admins."

/**
 * A reusable component for showing upgrade prompts to free tier users
 */
export default function UpgradeCTA({
  variant = 'banner',
  message = 'Upgrade to a paid plan to unlock more features',
  isOpen = false,
  onClose = () => {},
  ctaText = 'Upgrade Now',
  restrictedFeature,
}: UpgradeCTAProps) {
  // Navigation hook
  const navigate = useNavigate()

  // Get user permissions to check if they're an org admin
  const { permissions } = usePermissions()

  // Determine if upgrade should be disabled
  // - No permissions (null) = free tier user without org → show upgrade button
  // - permissions.role === 'admin' → show upgrade button
  // - permissions.role === 'user' → show request-upgrade button instead
  const shouldDisableUpgrade = permissions?.role === 'user'

  // URL for the pricing page
  const upgradePath = '/account' // Assuming the account page has pricing options

  // Handle upgrade button click
  const handleUpgradeClick = () => {
    navigate(upgradePath)
    onClose()
  }

  // Non-admins can't upgrade directly, but can notify their org admin(s)
  const { user, token, orgs } = getCurrentUser()
  const orgUuid = getEffectiveOrgUuid(user, orgs)
  const requestUpgradeMutation = useRequestUpgrade()

  const handleRequestUpgrade = () => {
    if (!orgUuid || !token) return
    requestUpgradeMutation.mutate({ orgUuid: orgUuid as UUID, token })
  }

  const requestUpgradeSucceeded =
    shouldDisableUpgrade && requestUpgradeMutation.isSuccess
  const requestUpgradeFailed =
    shouldDisableUpgrade && requestUpgradeMutation.isError

  // The mutation's success/error state otherwise survives closing and
  // reopening the dialog (it's not tied to the dialog's own lifecycle),
  // so a later, unrelated gated action would reopen straight into a stale
  // "request sent" state instead of the initial prompt.
  const { reset: resetRequestUpgrade } = requestUpgradeMutation
  useEffect(() => {
    if (isOpen) {
      resetRequestUpgrade()
    }
  }, [isOpen, resetRequestUpgrade])

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const errorMessageRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (requestUpgradeSucceeded) {
      closeButtonRef.current?.focus()
    }
  }, [requestUpgradeSucceeded])

  useEffect(() => {
    if (requestUpgradeFailed) {
      errorMessageRef.current?.focus()
    }
  }, [requestUpgradeFailed])

  // Base message with feature-specific addition if provided
  const fullMessage = restrictedFeature
    ? `${restrictedFeature} is only available on paid plans. ${message}`
    : message

  // Dialog variant
  if (variant === 'dialog') {
    // Simple message for application writing
    const isApplicationFeature = restrictedFeature
      ?.toLowerCase()
      .includes('application')

    const isEmailApplicationFeature = restrictedFeature
      ?.toLowerCase()
      .includes('email application')

    const dialogMessage = isEmailApplicationFeature
      ? 'You need to upgrade to email copies of applications to yourself.'
      : isApplicationFeature
        ? 'You need to upgrade to write more applications.'
        : fullMessage

    return (
      <Dialog open={isOpen} onClose={onClose}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {requestUpgradeSucceeded ? (
            <>
              <CheckCircleIcon color="success" /> Upgrade Request Sent
            </>
          ) : (
            <>
              <LockIcon color="primary" /> Upgrade Required
            </>
          )}
        </DialogTitle>
        {!requestUpgradeSucceeded && (
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {dialogMessage}
            </Typography>
            {shouldDisableUpgrade && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {NOT_ADMIN_DIALOG_MESSAGE}
              </Typography>
            )}
            {shouldDisableUpgrade && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                You can send this request once every 5 minutes.
              </Typography>
            )}
            {requestUpgradeFailed && (
              <Typography
                variant="body2"
                color="error"
                role="alert"
                ref={errorMessageRef}
                tabIndex={-1}
                sx={{ mt: 1, outline: 'none' }}
              >
                {requestUpgradeErrorMessage(requestUpgradeMutation.error)}
              </Typography>
            )}
          </DialogContent>
        )}
        <DialogActions sx={requestUpgradeSucceeded ? { p: 3 } : { p: 2, pt: 0 }}>
          {requestUpgradeSucceeded ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1.5,
                width: '100%',
              }}
            >
              <Box role="status">
                <Typography
                  variant="body2"
                  sx={{ color: 'success.main', fontWeight: 500 }}
                >
                  {REQUEST_UPGRADE_SUCCESS_MESSAGE}
                </Typography>
              </Box>
              <Button
                ref={closeButtonRef}
                onClick={onClose}
                variant="contained"
                color="primary"
                disableRipple
                sx={{
                  alignSelf: 'center',
                  '&:focus-visible': {
                    outline: '2px solid #0d47a1',
                    outlineOffset: '2px',
                  },
                }}
              >
                Close
              </Button>
            </Box>
          ) : (
            <>
              <Button
                onClick={onClose}
                color="inherit"
                disableRipple
                sx={{
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Not Now
              </Button>
              {shouldDisableUpgrade ? (
                <Button
                  variant="contained"
                  disableRipple
                  color="primary"
                  startIcon={<ArrowUpwardIcon />}
                  onClick={handleRequestUpgrade}
                  disabled={requestUpgradeMutation.isPending}
                >
                  Request Upgrade
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disableRipple
                  color="primary"
                  startIcon={<ArrowUpwardIcon />}
                  onClick={handleUpgradeClick}
                >
                  {ctaText}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    )
  }

  // Inline variant - more compact
  if (variant === 'inline') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          p: 2,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          bgcolor: '#f5f9ff',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="primary" fontSize="small" />
          <Typography variant="body2">
            {fullMessage}
            {shouldDisableUpgrade && ` ${CONTACT_ADMIN_MESSAGE}`}
          </Typography>
        </Box>
        {shouldDisableUpgrade ? (
          <AccessibleTooltip title={ADMIN_ONLY_TOOLTIP} {...whiteTooltipProps}>
            <span>
              <Button
                variant="outlined"
                disableRipple
                color="primary"
                size="small"
                disabled
                startIcon={<ArrowUpwardIcon />}
              >
                {ctaText}
              </Button>
            </span>
          </AccessibleTooltip>
        ) : (
          <Button
            variant="outlined"
            disableRipple
            color="primary"
            size="small"
            onClick={handleUpgradeClick}
            sx={{
              '&:focus': {
                backgroundColor: '#e8e8e8',
              },
            }}
            startIcon={<ArrowUpwardIcon />}
          >
            {ctaText}
          </Button>
        )}
      </Box>
    )
  }

  // Default banner variant
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 4,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        bgcolor: '#f5f9ff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LockIcon color="primary" /> You're on the Free Plan
          </Typography>
          <Typography variant="body1">{fullMessage}</Typography>
          {shouldDisableUpgrade && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {CONTACT_ADMIN_MESSAGE}
            </Typography>
          )}
        </Box>
        {shouldDisableUpgrade ? (
          <AccessibleTooltip title={ADMIN_ONLY_TOOLTIP} {...whiteTooltipProps}>
            <span>
              <Button
                variant="contained"
                disableRipple
                color="primary"
                disabled
                startIcon={<ArrowUpwardIcon />}
                sx={{
                  py: 1.2,
                  px: 3,
                  whiteSpace: 'nowrap',
                  fontSize: '0.95rem',
                }}
              >
                {ctaText}
              </Button>
            </span>
          </AccessibleTooltip>
        ) : (
          <Button
            variant="contained"
            disableRipple
            color="primary"
            onClick={handleUpgradeClick}
            startIcon={<ArrowUpwardIcon />}
            sx={{
              py: 1.2,
              px: 3,
              whiteSpace: 'nowrap',
              fontSize: '0.95rem',
            }}
          >
            {ctaText}
          </Button>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Free Plan Limits:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <Typography component="li">
              Limited to 5 matches per category
            </Typography>
            <Typography component="li">Maximum of 2 scans total</Typography>
            <Typography component="li">
              No access to upcoming opportunities
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Paid Plan Benefits:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <Typography component="li">Unlimited scans</Typography>
            <Typography component="li">Unlimited matches</Typography>
            <Typography component="li">Unlimited applications</Typography>
            <Typography component="li">
              Access to upcoming opportunities
            </Typography>
            <Typography component="li">Flexible payment options</Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Why Upgrade?
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <Typography component="li">
              Find more funding opportunities
            </Typography>
            <Typography component="li">
              Save time with advanced features
            </Typography>
            <Typography component="li">
              Increase your funding success rate
            </Typography>
            <Typography component="li">Premium support</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  )
}
